import { TrackPosition } from "utils/chart/trackPosition";

/** A point in pixels, e.g. from `map.project()` (Leaflet's `L.Point` is one) */
export type PixelPoint = { x: number; y: number };

const squaredDistance = (a: PixelPoint, b: PixelPoint) =>
  (a.x - b.x) ** 2 + (a.y - b.y) ** 2;

/** Where the point projects onto the segment a-b (0 at a, 1 at b) and its squared distance from there */
const projectOntoSegment = (
  point: PixelPoint,
  a: PixelPoint,
  b: PixelPoint
) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return { fraction: 0, distance: squaredDistance(point, a) };
  }

  const fraction = Math.max(
    0,
    Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared)
  );
  return {
    fraction,
    distance: squaredDistance(point, {
      x: a.x + fraction * dx,
      y: a.y + fraction * dy,
    }),
  };
};

/**
 * The place on the track line under the cursor, null when the cursor isn't on the line: the cursor is on the line
 * when it's within `tolerance` pixels of one of its segments. The place is where the cursor projects onto the
 * nearest segment.
 */
export const findHoveredTrackPosition = (
  points: PixelPoint[],
  cursor: PixelPoint,
  tolerance: number
): TrackPosition | null => {
  const toleranceSquared = tolerance * tolerance;

  if (points.length === 1) {
    return squaredDistance(cursor, points[0]) <= toleranceSquared
      ? { index: 0, fraction: 0 }
      : null;
  }

  let nearest: TrackPosition | null = null;
  let nearestDistance = Infinity;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    // Segments whose bounding box is farther than the tolerance can't be nearer, skipped cheaply
    if (
      cursor.x < Math.min(a.x, b.x) - tolerance ||
      cursor.x > Math.max(a.x, b.x) + tolerance ||
      cursor.y < Math.min(a.y, b.y) - tolerance ||
      cursor.y > Math.max(a.y, b.y) + tolerance
    ) {
      continue;
    }
    const { fraction, distance } = projectOntoSegment(cursor, a, b);
    // Of overlapping segments (a track going back the same way) the first one wins
    if (distance <= toleranceSquared && distance < nearestDistance) {
      nearestDistance = distance;
      nearest = { index: i - 1, fraction };
    }
  }
  return nearest;
};
